<script setup lang="ts">
import { useChatStore } from '@/stores/chatStore.ts'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import ChatMessages from '@/components/Sections/Communication/ChatRoom/ChatMessages.vue'
import ChatInput from '@/components/Sections/Communication/ChatRoom/ChatInput.vue'
import ChatRoomHeader from '@/components/Sections/Communication/ChatRoom/ChatRoomHeader.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores
 */
const chatStore = useChatStore()

/**
 * Computed
 */
const chatRoom = computed(() => {
  return chatStore.getRoomById(chatStore.activeRoomId)
})

/**
 * Mark room as read when opening it
 */
const markCurrentRoomAsRead = () => {
  if (chatStore.activeRoomId > 0 && chatStore.roomMessages.has(chatStore.activeRoomId)) {
    const messages = chatStore.roomMessages.get(chatStore.activeRoomId)
    if (messages && messages.size > 0) {
      chatStore.setRoomAsRead(chatStore.activeRoomId)
    }
  }
}

/**
 * Lifecycle
 */
onMounted(() => {
  chatStore.setVisibleRoomId(chatStore.activeRoomId)
  // Mark current room as read when component mounts
  markCurrentRoomAsRead()
})

onUnmounted(() => {
  // Don't reset activeRoomId to 0 - let it persist so user returns to same room
  // chatStore.setActiveRoomId(0)
})

/**
 * Watch for room changes and mark as read
 */
watch(() => chatStore.activeRoomId, (newRoomId) => {
  if (newRoomId > 0) {
    // Mark the newly opened room as read
    markCurrentRoomAsRead()
  }
})

</script>

<template>
  <div v-if="chatRoom" class="flex-grow flex flex-col mr-2 height-limit">
    <ChatRoomHeader />
    <ChatMessages />
    <ChatInput />
  </div>
  <StandardCard v-else :title="t('communication.chat.yourChatRooms')" class="mr-2">
    <p>
      {{ t('communication.chat.chatRoomsDescription1') }}
    </p>
    <p>
      {{ t('communication.chat.chatRoomsDescription2') }}
    </p>
  </StandardCard>
</template>

<style scoped lang="scss"></style>
